import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const EMPTY_SECTION = { heading: "", body: "" };
const EMPTY_PAGE = {
  slug: "",
  title: "",
  meta_description: "",
  hero_image: "",
  hero_subtitle: "",
  sections: [{ ...EMPTY_SECTION }],
  published: true,
};

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdminCustomPagesEditor() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // page being edited
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_PAGE);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/custom-pages");
      setPages(data);
    } catch (e) {
      toast.error("Failed to load custom pages", { description: formatErrorDetail(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setDraft({ ...EMPTY_PAGE, sections: [{ ...EMPTY_SECTION }] });
    setActive(null);
    setCreating(true);
  }

  function startEdit(page) {
    setDraft({
      slug: page.slug,
      title: page.title || "",
      meta_description: page.meta_description || "",
      hero_image: page.hero_image || "",
      hero_subtitle: page.hero_subtitle || "",
      sections: page.sections && page.sections.length ? page.sections.map(s => ({ ...s })) : [{ ...EMPTY_SECTION }],
      published: page.published !== false,
    });
    setActive(page.slug);
    setCreating(false);
  }

  function cancel() {
    setActive(null);
    setCreating(false);
    setDraft(EMPTY_PAGE);
  }

  async function save() {
    if (!draft.title.trim()) {
      toast.error("Please enter a page title");
      return;
    }
    const slug = slugify(draft.slug || draft.title);
    if (!slug) {
      toast.error("Please enter a valid slug");
      return;
    }
    setSaving(true);
    try {
      if (creating) {
        const payload = { ...draft, slug };
        await api.post("/admin/custom-pages", payload);
        toast.success("Page created", { description: `Live at /pages/${slug}` });
      } else {
        const { slug: _unused, ...patch } = draft;
        await api.put(`/admin/custom-pages/${active}`, patch);
        toast.success("Page updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error("Save failed", { description: formatErrorDetail(e) });
    } finally {
      setSaving(false);
    }
  }

  async function togglePublished(page) {
    try {
      await api.put(`/admin/custom-pages/${page.slug}`, { published: !page.published });
      toast.success(page.published ? "Page unpublished" : "Page published");
      load();
    } catch (e) {
      toast.error("Toggle failed", { description: formatErrorDetail(e) });
    }
  }

  async function remove(page) {
    try {
      await api.delete(`/admin/custom-pages/${page.slug}`);
      toast.success(`Deleted "${page.title}"`);
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatErrorDetail(e) });
    }
  }

  function updateSection(i, patch) {
    setDraft(d => ({
      ...d,
      sections: d.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s),
    }));
  }

  function addSection() {
    setDraft(d => ({ ...d, sections: [...d.sections, { ...EMPTY_SECTION }] }));
  }

  function removeSection(i) {
    setDraft(d => ({
      ...d,
      sections: d.sections.length > 1 ? d.sections.filter((_, idx) => idx !== i) : d.sections,
    }));
  }

  const editorOpen = creating || active !== null;

  return (
    <div data-testid="admin-custom-pages">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl">Custom Pages</h2>
          <p className="text-sm text-foreground/60 mt-1">Add your own info / landing pages. They live at <code className="bg-cream-100 px-1 rounded">/pages/&#123;slug&#125;</code> and appear in the sitemap.</p>
        </div>
        <button
          onClick={startCreate}
          data-testid="admin-custom-pages-new-btn"
          className="inline-flex items-center gap-2 bg-deepred text-white px-4 py-2 text-sm uppercase tracking-wider hover:bg-saffron transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Page
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-deepred" /></div>
      ) : pages.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-cream-300 rounded-sm" data-testid="admin-custom-pages-empty">
          <div className="text-foreground/60 mb-4">No custom pages yet.</div>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 bg-deepred text-white px-6 py-3 text-sm uppercase tracking-wider hover:bg-saffron transition-colors"
          >
            <Plus className="w-4 h-4" /> Create your first page
          </button>
        </div>
      ) : (
        <div className="border border-cream-200 rounded-sm overflow-hidden" data-testid="admin-custom-pages-list">
          <table className="w-full text-sm">
            <thead className="bg-cream-100 text-left text-xs uppercase tracking-wider text-foreground/60">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map(p => (
                <tr key={p.slug} className="border-t border-cream-100" data-testid={`admin-custom-page-row-${p.slug}`}>
                  <td className="px-4 py-3 font-medium">{p.title}</td>
                  <td className="px-4 py-3 text-foreground/70"><code>/pages/{p.slug}</code></td>
                  <td className="px-4 py-3">
                    <button onClick={() => togglePublished(p)} className={`text-xs uppercase tracking-wider px-2 py-1 rounded-sm inline-flex items-center gap-1 ${p.published ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`} data-testid={`admin-custom-page-publish-${p.slug}`}>
                      {p.published ? <><Eye className="w-3 h-3" /> Published</> : <><EyeOff className="w-3 h-3" /> Draft</>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-foreground/60 text-xs">{p.updated_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Link to={`/pages/${p.slug}`} target="_blank" className="inline-flex items-center gap-1 text-xs text-foreground/70 hover:text-deepred" data-testid={`admin-custom-page-view-${p.slug}`}>
                      <ExternalLink className="w-3 h-3" /> View
                    </Link>
                    <button onClick={() => startEdit(p)} className="text-xs uppercase tracking-wider text-deepred hover:underline" data-testid={`admin-custom-page-edit-${p.slug}`}>Edit</button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-xs uppercase tracking-wider text-red-600 hover:underline" data-testid={`admin-custom-page-delete-${p.slug}`}>Delete</button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete &ldquo;{p.title}&rdquo;?</AlertDialogTitle>
                          <AlertDialogDescription>This removes the page and its URL from your site and sitemap. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(p)} className="bg-red-600 hover:bg-red-700" data-testid={`admin-custom-page-delete-confirm-${p.slug}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={(o) => { if (!o) cancel(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-custom-page-editor">
          <DialogHeader>
            <DialogTitle>{creating ? "Add a new page" : `Edit “${draft.title}”`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={draft.title}
                  onChange={(e) => setDraft(d => ({ ...d, title: e.target.value, slug: creating && !d.slug ? slugify(e.target.value) : d.slug }))}
                  placeholder="e.g. Why book with us"
                  data-testid="custom-page-title"
                />
              </div>
              <div>
                <Label>URL slug</Label>
                <Input
                  value={draft.slug}
                  onChange={(e) => setDraft(d => ({ ...d, slug: slugify(e.target.value) }))}
                  placeholder="why-book-with-us"
                  disabled={!creating}
                  data-testid="custom-page-slug"
                />
                <p className="text-xs text-foreground/55 mt-1">Will be reachable at <code>/pages/{draft.slug || "your-slug"}</code></p>
              </div>
            </div>
            <div>
              <Label>Meta description (for SEO, 120–160 chars)</Label>
              <Textarea
                value={draft.meta_description}
                onChange={(e) => setDraft(d => ({ ...d, meta_description: e.target.value }))}
                maxLength={200}
                rows={2}
                placeholder="A concise, distinctive summary of the page — appears in Google search snippets."
                data-testid="custom-page-meta-description"
              />
              <p className="text-xs text-foreground/55 mt-1">{draft.meta_description.length}/200</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hero image URL</Label>
                <Input
                  value={draft.hero_image}
                  onChange={(e) => setDraft(d => ({ ...d, hero_image: e.target.value }))}
                  placeholder="https://…"
                  data-testid="custom-page-hero-image"
                />
              </div>
              <div>
                <Label>Hero subtitle</Label>
                <Input
                  value={draft.hero_subtitle}
                  onChange={(e) => setDraft(d => ({ ...d, hero_subtitle: e.target.value }))}
                  placeholder="Short line under the title"
                  data-testid="custom-page-hero-subtitle"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Content sections</Label>
                <button
                  onClick={addSection}
                  className="text-xs uppercase tracking-wider text-deepred hover:underline inline-flex items-center gap-1"
                  data-testid="custom-page-add-section"
                >
                  <Plus className="w-3 h-3" /> Add section
                </button>
              </div>
              <div className="space-y-4">
                {draft.sections.map((s, i) => (
                  <div key={i} className="border border-cream-200 p-3 rounded-sm bg-cream-50" data-testid={`custom-page-section-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider text-foreground/60">Section {i + 1}</span>
                      {draft.sections.length > 1 && (
                        <button
                          onClick={() => removeSection(i)}
                          className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`custom-page-remove-section-${i}`}
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      )}
                    </div>
                    <Input
                      value={s.heading}
                      onChange={(e) => updateSection(i, { heading: e.target.value })}
                      placeholder="Section heading"
                      className="mb-2"
                      data-testid={`custom-page-section-${i}-heading`}
                    />
                    <Textarea
                      value={s.body}
                      onChange={(e) => updateSection(i, { body: e.target.value })}
                      placeholder="Section body — supports multi-line text"
                      rows={5}
                      data-testid={`custom-page-section-${i}-body`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-cream-200">
              <Switch checked={draft.published} onCheckedChange={(v) => setDraft(d => ({ ...d, published: v }))} data-testid="custom-page-published" />
              <div>
                <div className="text-sm font-medium">{draft.published ? "Published — publicly visible" : "Draft — not yet public"}</div>
                <div className="text-xs text-foreground/55">Toggle off to hide from visitors and sitemap.</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={cancel} className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground" data-testid="custom-page-cancel">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-deepred text-white px-5 py-2 text-sm uppercase tracking-wider hover:bg-saffron transition-colors disabled:opacity-50"
              data-testid="custom-page-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {creating ? "Create page" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
